<template>
  <div>
    <DpplNestedDropdown
      :children="dropdownItems"
      top-level-direction="bottom right"
      is-top
      :disabled="!isDataLoaded"
      @top-mouse-enter="$emit('tracking-event', {
        action: 'editorDropdownHover',
        args: { label: 'Share' },
      })"
    >
      <template #topLabel>
        <v-btn
          variant="flat"
          class="share-button"
          color="primary"
          rounded="lg"
          :disabled="!isDataLoaded"
        >
          <template #prepend>
            <v-icon
              icon="material-symbols:arrow-top-right-rounded"
              size="20"
            />
          </template>
          Share
        </v-btn>
      </template>
    </DpplNestedDropdown>
    <ShareModal
      :show="shareModalVisible"
      :job-id="jobId"
      :has-share-users="hasShareUsers"
      :valid-users="validUsers"
      :is-linked="isLinked"
      :show-invite="showInvite"
      :company-name="companyName"
      @close="shareModalVisible = false"
    />
    <NotifyModal
      :user="selectedUser"
      :show="notifyModalVisible"
      @notify="notify"
      @close="notifyModalVisible = false"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { camelCase } from 'lodash-es';
import { vueNotify, vueOnboard } from '@/api/vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import NotifyModal from '@/components/editor/NotifyModal.vue';
import ShareModal from '@/components/editor/ShareModal.vue';
import DpplNestedDropdown from '@/components/datapeople/DpplNestedDropdown/DpplNestedDropdown.vue';
import SmileIcon from '@/assets/icon-dppl-smile.svg?component';

const props = defineProps({
  jobId: { type: [String, Number], default: '' },
  ownersActive: { type: Array, default: () => [] },
  ownersLead: { type: Array, default: () => [] },
  validUsers: { type: Array, default: () => [] },
  isLinked: { type: Number, default: 0 },
  hasShareUsers: { type: Boolean, default: false },
  showInvite: { type: Boolean, default: false },
  companyName: { type: String, default: '' },
  isDataLoaded: { type: Boolean, default: false },
});

const emits = defineEmits(['tracking-event']);

const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const selectedUser = ref(null);
const shareModalVisible = ref(false);
const notifyModalVisible = ref(false);

const toggleNotifyModal = (bool, user) => {
  notifyModalVisible.value = bool;
  selectedUser.value = user;
};

const notify = async ({ userId, type, name }) => {
  try {
    const responseJSON = await vueNotify({
      userId,
      jobId: props.jobId,
      notificationType: type,
    });

    emits('tracking-event', {
      action: 'notify',
      args: { job: props, props: { name, type: type === 1 ? 'Request Edits' : 'Notify Complete' } },
    });

    if (responseJSON.success === true) {
      addSuccessSnackbar({
        text: 'Good news! Your email has successfully been sent.',
        timeout: 3500,
      });
    }
  } catch (e) {
    addErrorSnackbar({
      text: 'We\'re sorry but an error has occurred. Please try again.',
    });
    throw e;
  }
};

const onboard = async (userId, name) => {
  emits('tracking-event', {
    action: 'editorLinkClick',
    args: { label: 'Invite to Datapeople' },
  });

  try {
    const responseJSON = await vueOnboard({
      userId,
    });

    emits('tracking-event', {
      action: 'notify',
      args: { job: props, props: { name, type: 'Onboard' } },
    });

    if (responseJSON.success === true) {
      addSuccessSnackbar({
        text: 'Good news! Your email has successfully been sent.',
        timeout: 3500,
      });
    }
  } catch (e) {
    addErrorSnackbar({
      text: 'We\'re sorry but an error has occurred. Please try again.',
    });
    throw e;
  }
};

const dropdownItems = computed(() => ([
  ...props.ownersActive.map((activeOwner) => ({
    name: camelCase(activeOwner.name),
    label: activeOwner.name,
    onClick() {
      toggleNotifyModal(true, activeOwner);
    },
    prependIcon: SmileIcon,
    prependIconClass: 'icon-skills notify-dropdown-prepend-icon-color-blue mr-2',
  })),
  ...props.ownersLead.map((lead) => ({
    name: camelCase(lead.name),
    label: lead.name,
    prependIcon: SmileIcon,
    prependIconClass: 'icon-skills notify-dropdown-prepend-icon-color-gray mr-2',
    children: [
      {
        name: 'inviteToDatapeople',
        label: 'Invite to Datapeople',
        onClick() {
          onboard(lead.id, lead.name);
        },
      },
    ],
  })),
  {
    name: 'shareWithSomeone',
    label: 'Share with Someone',
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Share with Someone' },
      });

      shareModalVisible.value = true;
    },
  },
]));
</script>

<style lang="less">
.notify-dropdown-prepend-icon-color-blue {
  color: @text-brand;
}

.notify-dropdown-prepend-icon-color-gray {
  color: @grey-400;
}
</style>

<style scoped lang="less">
.dropdown-open .share-plus-icon {
  color: @text-brand;
}
.share-button {
  text-transform: none;
}
</style>
